/**
 * Wishlist — localStorage-based with slide-in drawer
 */

const Wishlist = (() => {
  const KEY = 'wishlist';

  function getAll() {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; }
    catch { return []; }
  }

  function save(list) { localStorage.setItem(KEY, JSON.stringify(list)); }
  function has(handle) { return getAll().includes(handle); }

  function add(handle) {
    const list = getAll();
    if (!list.includes(handle)) { list.push(handle); save(list); }
  }

  function remove(handle) { save(getAll().filter(h => h !== handle)); }

  function toggle(handle) {
    const wasIn = has(handle);
    wasIn ? remove(handle) : add(handle);
    document.dispatchEvent(new CustomEvent('wishlist:changed', { detail: { handle, added: !wasIn } }));
  }

  function count() { return getAll().length; }

  return { getAll, has, add, remove, toggle, count };
})();

const wishlistStrings = {
  title: 'Wishlist',
  close: 'Close wishlist',
  empty: 'Your wishlist is empty',
  continueShopping: 'Continue Shopping',
  noProducts: 'No products found.',
  viewPrint: 'View Print',
  remove: 'Remove',
  addToWishlist: 'Add to wishlist',
  removeFromWishlist: 'Remove from wishlist',
  ...(window.themeStrings && window.themeStrings.wishlist ? window.themeStrings.wishlist : {})
};

function createDrawer() {
  if (document.getElementById('wishlist-drawer')) return;

  const wrapper = document.createElement('div');
  wrapper.innerHTML = `
    <div id="wishlist-drawer-overlay" class="wishlist-overlay" aria-hidden="true"></div>
    <div id="wishlist-drawer" class="wishlist-drawer" aria-hidden="true" role="dialog" aria-label="${escAttr(wishlistStrings.title)}">
      <div class="wishlist-drawer__header">
        <span class="wishlist-drawer__title">${escHtml(wishlistStrings.title)}</span>
        <button class="wishlist-drawer__close" id="wishlist-drawer-close" aria-label="${escAttr(wishlistStrings.close)}">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div class="wishlist-drawer__body" id="wishlist-drawer-body" data-scrollable></div>
    </div>
  `;

  document.body.appendChild(wrapper.firstElementChild);
  document.body.appendChild(wrapper.lastElementChild);

  document.getElementById('wishlist-drawer-close').addEventListener('click', closeDrawer);
  document.getElementById('wishlist-drawer-overlay').addEventListener('click', closeDrawer);
}

function openDrawer() {
  renderDrawerContent();
  const drawer = document.getElementById('wishlist-drawer');
  const overlay = document.getElementById('wishlist-drawer-overlay');
  drawer.setAttribute('aria-hidden', 'false');
  drawer.classList.add('is-open');
  overlay.classList.add('is-visible');
  document.body.classList.add('wishlist-drawer-open');
}

function closeDrawer() {
  const drawer = document.getElementById('wishlist-drawer');
  const overlay = document.getElementById('wishlist-drawer-overlay');
  drawer.setAttribute('aria-hidden', 'true');
  drawer.classList.remove('is-open');
  overlay.classList.remove('is-visible');
  document.body.classList.remove('wishlist-drawer-open');
}

async function renderDrawerContent() {
  const body = document.getElementById('wishlist-drawer-body');
  if (!body) return;

  const handles = Wishlist.getAll();

  if (handles.length === 0) {
    body.innerHTML = `
      <div class="wishlist-empty">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="#ccc" stroke-width="1.2" aria-hidden="true">
          <path d="M16.5 3C14.76 3 13.09 3.81 12 5.09 10.91 3.81 9.24 3 7.5 3 4.42 3 2 5.42 2 8.5c0 3.78 3.4 6.86 8.55 11.54L12 21.35l1.45-1.32C18.6 15.36 22 12.28 22 8.5 22 5.42 19.58 3 16.5 3z"/>
        </svg>
        <p>${escHtml(wishlistStrings.empty)}</p>
        <button class="Button Button--primary" id="wishlist-continue-shopping">${escHtml(wishlistStrings.continueShopping)}</button>
      </div>`;
    document.getElementById('wishlist-continue-shopping').addEventListener('click', closeDrawer);
    return;
  }

  body.innerHTML = '<div class="wishlist-loading"><span class="spinner"></span></div>';

  const products = await Promise.all(
    handles.map(h =>
      fetch(`/products/${h}.js`).then(r => r.ok ? r.json() : null).catch(() => null)
    )
  );

  const valid = products.filter(Boolean);

  if (valid.length === 0) {
    body.innerHTML = `<div class="wishlist-empty"><p>${escHtml(wishlistStrings.noProducts)}</p></div>`;
    return;
  }

  body.innerHTML = `<div class="wishlist-drawer-list">${valid.map(p => buildItemHTML(p)).join('')}</div>`;
}

function buildItemHTML(product) {
  const image = product.featured_image
    ? `<img src="${product.featured_image}" alt="${escHtml(product.title)}" loading="lazy">`
    : '<div class="wishlist-no-image"></div>';

  const vendor = product.vendor ? `<p class="wishlist-drawer-item__vendor">${escHtml(product.vendor)}</p>` : '';

  return `
    <div class="wishlist-drawer-item" data-handle="${escAttr(product.handle)}">
      <a class="wishlist-drawer-item__img" href="/products/${escAttr(product.handle)}">${image}</a>
      <div class="wishlist-drawer-item__info">
        <a href="/products/${escAttr(product.handle)}" class="wishlist-drawer-item__title">${escHtml(product.title)}</a>
        ${vendor}
        <div class="wishlist-drawer-item__actions">
          <a href="/products/${escAttr(product.handle)}" class="wishlist-drawer-item__view-link">${escHtml(wishlistStrings.viewPrint)}</a>
          <button class="wishlist-drawer-item__remove" data-handle="${escAttr(product.handle)}" aria-label="${escAttr(wishlistStrings.removeFromWishlist)}">${escHtml(wishlistStrings.remove)}</button>
        </div>
      </div>
    </div>`;
}

function updateHeartButtons(changedHandle) {
  const selector = changedHandle
    ? `.wishlist-btn[data-handle="${changedHandle}"]`
    : '.wishlist-btn[data-handle]';

  document.querySelectorAll(selector).forEach(btn => {
    const active = Wishlist.has(btn.dataset.handle);
    btn.classList.toggle('wishlist-btn--active', active);
    btn.setAttribute('aria-label', active ? wishlistStrings.removeFromWishlist : wishlistStrings.addToWishlist);
    btn.setAttribute('aria-pressed', String(active));
  });
}

function updateWishlistCounter() {
  const hasItems = Wishlist.count() > 0;
  document.querySelectorAll('[data-action="open-wishlist"]').forEach(btn => {
    btn.classList.toggle('wishlist-header-active', hasItems);
  });
}

function escHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function escAttr(str) {
  return String(str).replace(/"/g, '&quot;');
}

document.addEventListener('click', function (e) {
  const removeBtn = e.target.closest('.wishlist-drawer-item__remove');
  if (removeBtn) {
    const handle = removeBtn.dataset.handle;
    Wishlist.remove(handle);
    const item = removeBtn.closest('.wishlist-drawer-item');
    item.style.transition = 'opacity 0.2s, max-height 0.3s';
    item.style.opacity = '0';
    item.style.overflow = 'hidden';
    item.style.maxHeight = item.offsetHeight + 'px';
    requestAnimationFrame(() => { item.style.maxHeight = '0'; });
    setTimeout(() => {
      item.remove();
      updateHeartButtons(handle);
      updateWishlistCounter();
      const list = document.querySelector('.wishlist-drawer-list');
      if (list && !list.querySelector('.wishlist-drawer-item')) {
        renderDrawerContent();
      }
    }, 300);
    return;
  }

  const btn = e.target.closest('.wishlist-btn');
  if (btn && !btn.closest('#wishlist-drawer')) {
    e.preventDefault();
    e.stopPropagation();
    const handle = btn.dataset.handle;
    if (!handle) return;
    Wishlist.toggle(handle);
    return;
  }

  if (e.target.closest('[data-action="open-wishlist"]')) {
    e.preventDefault();
    openDrawer();
  }
});

document.addEventListener('wishlist:changed', function (e) {
  updateHeartButtons(e.detail.handle);
  updateWishlistCounter();
});

let _wlTimer = null;
const _wlObserver = new MutationObserver((mutations) => {
  let found = false;
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (node.nodeType !== 1) continue;
      if ((node.matches && node.matches('.wishlist-btn[data-handle]')) ||
          (node.querySelector && node.querySelector('.wishlist-btn[data-handle]'))) {
        found = true;
        break;
      }
    }
    if (found) break;
  }
  if (!found) return;
  clearTimeout(_wlTimer);
  _wlTimer = setTimeout(() => {
    updateHeartButtons();
    updateWishlistCounter();
  }, 100);
});

document.addEventListener('DOMContentLoaded', function () {
  createDrawer();
  updateHeartButtons();
  updateWishlistCounter();
  _wlObserver.observe(document.body, { childList: true, subtree: true });
});
